import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { TabObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import {
  WidgetAnalysisGroupDataSource,
  WidgetKeyDriverAnalysisDataSource,
} from 'app/modules/widget/components/widget-data-source.component';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { WidgetDataSource } from '../../../../models';

@EnableClassTelemetry()
@Component({
  selector: 'app-datasource-selector',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './datasource-selector.component.html',
  styleUrls: ['./datasource-selector.component.scss'],
})
export class DatasourceSelectorComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() dataSources;
  @Input() previousDataSourceId;

  @Output() dataSourceSelect = new EventEmitter();

  selectorTabs: TabObject[] = [
    { title: 'Analyses', id: 'analyses' },
    { title: 'Analysis Groups', id: 'groups' },
  ];
  selectedTab: 'analyses' | 'groups' = 'analyses';

  searchValue: string;
  search$ = new Subject<string>();

  collections: any = {};
  collectionsArray = [];
  filteredCollectionsArray: any = [];
  analysisGroups: WidgetAnalysisGroupDataSource[] = [];
  filteredAnalysisGroupsArray: any = [];

  selectedDataSource: WidgetDataSource;

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.filterDataSources());

    this.selectorTabs.forEach((key) => {
      switch (key.id) {
        case 'analyses':
          key.title = translate('datasource-selector-component.analyses');
          break;
        case 'groups':
          key.title = translate('datasource-selector-component.analysis-groups');
          break;
      }
    });

    for (const key in this.dataSources) {
      if (this.dataSources.hasOwnProperty(key)) {
        if (key === '--groups--') {
          this.analysisGroups = this.dataSources[key];
        } else {
          const nonKdaDataSources = this.dataSources[key].filter(
            (ds) => !(ds instanceof WidgetKeyDriverAnalysisDataSource) && !ds.isDriverAnalysis
          );
          if (nonKdaDataSources.length > 0) {
            this.collections[key] = nonKdaDataSources;
          }
        }
        if (this.previousDataSourceId) {
          for (const dataSource of this.dataSources[key]) {
            if (dataSource.id === this.previousDataSourceId) {
              this.setWidgetDataSource(dataSource);
            }
          }
        }
      }
    }

    // Convert collections object into iterable array
    this.collectionsArray = [];
    for (const [key, value] of Object.entries(this.collections)) {
      const collectionValue = value as any;
      collectionValue.collectionId = key;
      this.collectionsArray.push(collectionValue);
    }

    for (const collection of this.collectionsArray) {
      const found = collection.find((dataSource) => dataSource.id === this.previousDataSourceId);
      if (found) {
        collection.previouslySelected = true;
        collection.isExpanded = true;
      } else {
        collection.previouslySelected = false;
        collection.isExpanded = false;
      }
    }
    this.filteredCollectionsArray = [...this.collectionsArray];
    this.filteredAnalysisGroupsArray = [...this.analysisGroups];

    for (const tab of this.selectorTabs) {
      tab.id === 'analyses'
        ? (tab.title = translate('datasource-selector-component.analyses-x', {
            x: this.analysesCount,
          }))
        : (tab.title = translate('datasource-selector-component.analyses-groups-x', {
            x: this.analysisGroupCount,
          }));
    }
  }

  public onSearch(value: string) {
    this.searchValue = value.trim();
    this.search$.next(this.searchValue);
  }

  public get analysisGroupCount() {
    return this.analysisGroups.length;
  }

  public get analysesCount() {
    if (!this.collections) {
      return 0;
    }
    let count = 0;
    for (const [_key, value] of Object.entries(this.collections)) {
      const collectionValue = value as Array<WidgetDataSource>;
      count += collectionValue.length;
    }
    return count;
  }

  setWidgetDataSource(dataSource: WidgetDataSource) {
    this.selectedDataSource = dataSource;

    this.dataSourceSelect.emit(this.selectedDataSource);
  }

  onToggleDropdown(collection) {
    collection.isExpanded = !collection.isExpanded;
  }

  onSelectorTabChange(id: 'analyses' | 'groups') {
    this.selectedTab = id;
  }

  filterDataSources() {
    if (this.searchValue.length >= 1) {
      this.filteredCollectionsArray = [];
      this.filteredAnalysisGroupsArray = [];

      //Search collections
      for (const collection of this.collectionsArray) {
        const collectionDisplayName = collection[0].collectionDisplayName;

        if (collectionDisplayName.toLowerCase().includes(this.searchValue.toLowerCase())) {
          this.filteredCollectionsArray.push(collection);
        }
        //Check if datasource display names within collection match
        for (const dataSource of collection) {
          if (dataSource.displayName.toLowerCase().includes(this.searchValue.toLowerCase())) {
            //Make sure this is not a duplicate match
            const found = this.filteredCollectionsArray.find(
              (item) => item.collectionId === dataSource.collectionName
            );

            if (!found) {
              this.filteredCollectionsArray.push(collection);
            }
          }
        }
      }

      //Search analysis groups
      for (const group of this.analysisGroups) {
        const displayName = group.displayName;
        if (displayName.toLowerCase().includes(this.searchValue.toLowerCase())) {
          this.filteredAnalysisGroupsArray.push(group);
        }
      }
    } else {
      this.filteredCollectionsArray = this.collectionsArray;
      this.filteredAnalysisGroupsArray = this.analysisGroups;
    }
  }

  ngOnDestroy() {
    this.search$.unsubscribe();
  }
}
